import {Menu, X, LogOut, Search} from 'lucide-react';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { lumi } from '@/lib/lumi';
import Logo from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';

const getNavKeyFromPath = (pathname: string): string | null => {
  pathname = pathname.replace(/\/+$/, '') || '/';
  if (pathname === '/cadastro') return 'cadastro';
  if (pathname === '/vagas') return 'vagas';
  if (pathname === '/gastronomia') return 'gastronomia';
  if ((pathname === '/marketplace' || pathname.startsWith('/marketplace/'))) return 'marketplace';
  if (pathname === '/feed') return 'feed';
  return null;
};

// Guarda o item ativo anterior fora do componente para sobreviver a remontagens
// da Navbar durante a troca de rota, garantindo que a pilula parta da posicao real.
let lastNavKey: string | null = null;

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAuthenticated, signOut } = useAuth();
  const location = useLocation();
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [profileType, setProfileType] = useState<string>('');
  const ringColorClass = profileType === 'restaurante' ? 'ring-[#E53935]' : 'ring-white';
  const [isScrolled, setIsScrolled] = useState(false);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [activeKey, setActiveKey] = useState<string>(() => getNavKeyFromPath(location.pathname) || '');
  const [pillStyle, setPillStyle] = useState<{left: number;width: number;}>({ left: 0, width: 0 });
  const [pillDuration, setPillDuration] = useState<number>(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const key = getNavKeyFromPath(location.pathname);
    setActiveKey(key || '');
    setIsMenuOpen(false);
  }, [location.pathname]);

  const measureNavItem = (key: string): {left: number;width: number;} | null => {
    const el = navItemRefs.current[key];
    const container = navContainerRef.current;
    if (!el || !container) return null;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return { left: elRect.left - containerRect.left, width: elRect.width };
  };

  useLayoutEffect(() => {
    const target = measureNavItem(activeKey);
    if (!target) { setPillStyle({ left: 0, width: 0 }); return; }

    const origin = lastNavKey && lastNavKey !== activeKey ? measureNavItem(lastNavKey) : null;
    lastNavKey = activeKey;

    if (!origin || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPillDuration(0);
      setPillStyle(target);
      return;
    }

    // 1) Posiciona instantaneamente na origem real (item que estava ativo)
    setPillDuration(0);
    setPillStyle(origin);

    // 2) No frame seguinte, anima ate o destino na direcao correta
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        const distance = Math.abs(target.left - origin.left);
        setPillDuration(Math.min(500 + distance * 1.6, 1400));
        setPillStyle(target);
      });
    });

    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) cancelAnimationFrame(innerRaf);
    };
  }, [activeKey, isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      const target = measureNavItem(activeKey);
      if (!target) return;
      setPillDuration(0);
      setPillStyle(target);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeKey, isAuthenticated]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setIsMenuOpen(false); };
    const onResize = () => { if (window.innerWidth >= 1280) setIsMenuOpen(false); };
    window.addEventListener('keydown', onKey); window.addEventListener('resize', onResize);
    return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); };
  }, [isMenuOpen]);

  const displayName = user?.userName || 'Perfil';
  const initials = displayName.
  split(' ').
  map((n: string) => n[0]).
  join('').
  toUpperCase().
  slice(0, 2);

  useEffect(() => {
    if (!user?.userId) {
      setProfileImageUrl(''); setProfileType('');
      return;
    }
    lumi.entities.user_profiles.list({ filter: { userId: user.userId } }).
    then((res: any) => {
      const profile = res?.list?.[0];
      if (profile?.profileImageUrl) setProfileImageUrl(profile.profileImageUrl);
      if (profile?.profileType) setProfileType(profile.profileType);
    }).
    catch(() => {});
  }, [user?.userId]);

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-gradient-to-r from-[#3E2723]/80 to-[#2A1A15]/80 backdrop-blur-xl shadow-2xl border-b border-[#5D4037]/50' : 'bg-gradient-to-r from-[#3D0F0A] via-[#0D0A09] to-[#000000] shadow-lg border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className={`flex-shrink-0 bg-transparent transition-opacity duration-200 ${isMenuOpen ? 'opacity-0 pointer-events-none xl:opacity-100 xl:pointer-events-auto' : 'opacity-100'}`}>
            <Logo size="md" className="bg-transparent" />
          </div>

          {/* Desktop: os mesmos links e a mesma pílula para visitantes e membros. */}
          <div className="hidden xl:flex items-center gap-3">
            <div ref={navContainerRef} className="relative flex items-center gap-1.5 bg-[#2A1A15]/60 backdrop-blur-md border border-[#5D4037]/60 rounded-full p-1.5">
              <div aria-hidden="true" className="absolute top-1.5 bottom-1.5 left-0 rounded-full bg-white shadow-md transition-all ease-out motion-reduce:transition-none will-change-transform" style={{ transform: `translateX(${pillStyle.left}px)`, width: `${pillStyle.width}px`, transitionDuration: `${pillDuration}ms` }} />
              {[
                ...(!isAuthenticated ? [{key: 'cadastro', label: 'Cadastro', to: '/cadastro'}] : []),
                {key: 'vagas', label: 'Vagas', to: '/vagas'},
                {key: 'gastronomia', label: 'O churrasco', to: '/gastronomia'},
                {key: 'marketplace', label: 'Marketplace', to: '/marketplace'},
                ...(isAuthenticated ? [{key: 'feed', label: 'Feed', to: '/feed'}] : []),
              ].map(item => <Link key={item.key} ref={el => { navItemRefs.current[item.key] = el; }} to={item.to}
                aria-current={activeKey === item.key ? 'page' : undefined}
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${activeKey === item.key ? 'text-[#2A1A15]' : 'text-gray-200 hover:text-white hover:bg-[#5D4037]/70'}`}>
                {item.label}
              </Link>)}
              <a ref={el => { navItemRefs.current.contato = el; }} href="#contato" onClick={() => setActiveKey('contato')} className={`relative z-10 px-4 py-2 rounded-full text-sm font-medium ${activeKey === 'contato' ? 'text-[#2A1A15]' : 'text-gray-200 hover:text-white'}`}>Contato</a>
            </div>
            {isAuthenticated ? <>
                <Link
                to="/buscar-usuarios"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2A1A15]/60 backdrop-blur-md border border-[#5D4037]/60 text-gray-100 hover:text-white hover:bg-[#5D4037]/70 hover:scale-105 transition-all shadow-lg"
                aria-label="Pesquisar usuarios"
                title="Pesquisar usuarios">

                  <Search size={18} />
                </Link>
                <ThemeToggle />
                <Link
                to="/perfil"
                className="flex items-center text-gray-100 hover:text-white transition-all font-medium"
                aria-label={displayName}
                title={displayName}>

                  {profileImageUrl ?
                <img
                  src={profileImageUrl}
                  alt={displayName}
                  className={`w-10 h-10 rounded-full object-cover shadow-lg ring-2 ${ringColorClass} ring-offset-2 ring-offset-[#2A1A15] hover:scale-105 transition-transform`} /> :

                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-xs font-bold border-2 border-[#8D0000] shadow-lg hover:scale-105 transition-transform">
                      {initials}
                    </div>
                }
                </Link>
                <button
                onClick={() => {
                  signOut();
                  window.location.href = '/';
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2A1A15]/60 backdrop-blur-md border border-[#5D4037]/60 text-gray-300 hover:text-[#E53935] hover:bg-[#5D4037]/70 transition-all font-medium text-sm">

                  <LogOut size={16} />
                  Sair
                </button>
            </> : <Link to="/cadastro" className="bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-6 py-2.5 rounded-full text-sm font-semibold">Entrar</Link>}
          </div>

          {/* Mobile Menu Button */}
          <div className="xl:hidden">
            <button
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-100 hover:text-[#E53935] transition-colors">

              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - painel deslizante da direita para a esquerda */}
        <div
          className={`xl:hidden fixed inset-0 z-[60] ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!isMenuOpen}
          {...(!isMenuOpen ? { inert: "" } : {})}>

          {/* Fundo escuro (10% restantes) - clique fecha o menu */}
          <div
            onClick={() => setIsMenuOpen(false)}
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${isMenuOpen ? 'opacity-95' : 'opacity-0'}`} />


          {/* Painel do menu ocupando 80% da largura */}
          <div
            className={`absolute top-0 right-0 h-full w-[80%] bg-[#150C0A] border-l border-[#5D4037]/60 shadow-2xl overflow-y-auto flex flex-col transition-transform duration-300 ease-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

            <div className="flex items-center justify-between gap-3 h-20 px-5 border-b border-[#5D4037]/40">
              {isAuthenticated ?
              <Link
                to="/perfil"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 min-w-0 text-gray-100 hover:text-white transition-colors"
                aria-label={displayName}
                title={displayName}>

                  {profileImageUrl ?
                <img
                  src={profileImageUrl}
                  alt={displayName}
                  className={`w-11 h-11 rounded-full object-cover shadow-lg ring-2 ${ringColorClass} ring-offset-2 ring-offset-[#150C0A]`} /> :

                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-xs font-bold border-2 border-[#8D0000] shadow-lg">
                      {initials}
                    </div>
                }
                  <span className="truncate font-semibold">{displayName}</span>
                </Link> :

              <Logo size="md" className="bg-transparent" />
              }
              <button
                onClick={() => setIsMenuOpen(false)}
                className="flex-shrink-0 text-gray-100 hover:text-[#E53935] transition-colors"
                aria-label="Fechar menu">

                <X size={26} />
              </button>
            </div>

            <div className="flex flex-col space-y-2 p-5">
              {!isAuthenticated ?
              <>
                  <Link to="/cadastro" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Cadastro
                  </Link>
                  <Link to="/vagas" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Vagas
                  </Link>
                  <Link to="/gastronomia" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    A gastronomia
                  </Link>
                  <Link to="/marketplace" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Marketplace
                  </Link>
                  <a href="#contato" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Contato
                  </a>
                  <Link to="/cadastro" className="bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all border border-[#8D0000] block text-center mt-2" onClick={() => setIsMenuOpen(false)}>
                    Entrar
                  </Link>
                </> :

              <>
                  <Link to="/buscar-usuarios" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Pesquisar usuarios
                  </Link>
                  <Link to="/vagas" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Vagas
                  </Link>
                  <Link to="/gastronomia" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    A gastronomia
                  </Link>
                  <Link to="/marketplace" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Marketplace
                  </Link>
                  <a href="#contato" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Contato
                  </a>
                  <Link to="/feed" className="text-gray-100 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3" onClick={() => setIsMenuOpen(false)}>
                    Feed
                  </Link>
                  <button
                  onClick={() => {
                    signOut();
                    setIsMenuOpen(false);
                    window.location.href = '/';
                  }}
                  className="flex items-center gap-2 text-gray-300 hover:text-[#E53935] hover:bg-[#5D4037]/40 rounded-xl transition-all font-medium px-3 py-3 mt-2">

                    <LogOut size={16} />
                    Sair
                  </button>
                </>
              }
            </div>

            {/* Alternador de tema claro/escuro no menu mobile */}
            <div className="px-5 pb-2 flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-gray-300">Tema escuro</span>
              <ThemeToggle />
            </div>

            {/* Rodape do menu mobile com o logo BBQ Jobs */}
            <div className="mt-auto border-t border-[#5D4037]/40 flex justify-center pt-[24px] pl-[20px] pr-[20px] pb-[24px]">
              <Logo size="md" className="bg-transparent" />
            </div>
          </div>
        </div>
      </div>
    </nav>);

}

export default Navbar;